// Class reminders and update notifications on this device (web push).
// Works in Chrome, Edge and Firefox, and on iPhone/iPad once the app is added to the Home Screen (iOS 16.4+).
import { AppError, rpc } from './api.js';
import { isIOS, isStandalone } from './state.js';

// 'ok', 'ios-install' (iPhone in Safari: add to Home Screen first) or 'unsupported'
export function pushSupport() {
  const has = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window && window.isSecureContext;
  if (has) return 'ok';
  if (isIOS() && !isStandalone()) return 'ios-install';
  return 'unsupported';
}

export function permission() {
  return 'Notification' in window ? Notification.permission : 'denied';
}

async function registration() {
  const reg = await Promise.race([
    navigator.serviceWorker.ready,
    new Promise((resolve) => setTimeout(() => resolve(null), 8000)),
  ]);
  if (!reg) throw new AppError('The app is still getting ready on this device. Close it, open it again and try once more.', 'not_ready');
  return reg;
}

export async function currentSubscription() {
  if (pushSupport() !== 'ok') return null;
  try {
    const reg = await registration();
    return await reg.pushManager.getSubscription();
  } catch (e) {
    return null;
  }
}

function keyBytes(base64url) {
  const pad = '='.repeat((4 - (base64url.length % 4)) % 4);
  const raw = atob((base64url + pad).replace(/-/g, '+').replace(/_/g, '/'));
  return Uint8Array.from(raw, (c) => c.charCodeAt(0));
}

function sameKey(sub, key) {
  const current = sub.options && sub.options.applicationServerKey;
  if (!current) return true; // the browser doesn't say: assume it is ours
  const a = new Uint8Array(current);
  const b = keyBytes(key);
  return a.length === b.length && a.every((x, i) => x === b[i]);
}

async function save(sub) {
  const json = sub.toJSON();
  await rpc('save_push_subscription', {
    p_endpoint: json.endpoint,
    p_p256dh: json.keys.p256dh,
    p_auth: json.keys.auth,
    p_user_agent: navigator.userAgent.slice(0, 300),
  });
}

// After each sign-in: this device's notifications go to whoever is signed in now (a shared laptop
// stops getting the previous person's), and a subscription the browser renewed is saved again.
export async function resyncNotifications() {
  try {
    if (pushSupport() !== 'ok' || permission() !== 'granted') return;
    const reg = await registration();
    let sub = await reg.pushManager.getSubscription();
    if (!sub) return;
    const key = await rpc('push_public_key');
    if (!key) return;
    if (!sameKey(sub, key)) {
      await sub.unsubscribe();
      sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
    }
    await save(sub);
  } catch (e) { /* not worth interrupting anyone: Profile shows whether reminders are on */ }
}

// Signed out, here or from another device: this device stops showing that person's notifications.
export async function dropLocalSubscription() {
  try {
    const sub = await currentSubscription();
    if (sub) await sub.unsubscribe();
  } catch (e) { /* ignore */ }
}

// Call straight from a tap: iPhone only shows the permission question in response to one.
export async function enableNotifications() {
  if (pushSupport() !== 'ok') throw new AppError('This browser can’t show notifications.', 'unsupported');
  const answer = await Notification.requestPermission();
  if (answer !== 'granted') {
    throw new AppError('Notifications are switched off for this app. Allow them in your phone’s settings, then try again.', 'denied');
  }
  const key = await rpc('push_public_key');
  if (!key) throw new AppError('Reminders are still being set up by the academy. Please try again in a few minutes.', 'not_ready');
  const reg = await registration();
  let sub = await reg.pushManager.getSubscription();
  if (sub && !sameKey(sub, key)) { await sub.unsubscribe(); sub = null; }
  if (!sub) sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: keyBytes(key) });
  await save(sub);
  return true;
}

export async function disableNotifications() {
  const sub = await currentSubscription();
  if (!sub) return;
  try { await rpc('delete_push_subscription', { p_endpoint: sub.endpoint }); } catch (e) { /* signed out or offline: the server forgets it later */ }
  await sub.unsubscribe();
}
